import settings from '@/../../settings/settings.json'

const defaultFields = {}
const primaryFields = {}
const defaultPrecisions = {}
const fieldPrecisions = { 'status': 0, 'rf-level': 0 }
const mpTypeFirstAlias = {}
const measurementType = {}
const measurementFields = {}
const seriesLabels = {}
const fields = {
    'status': {
        'name': 'status',
        'type': 'bit-field',
        'unit': '',
        'label': 'Status',
        'shortLabel': 'Status',
        'longLabel': 'Status',
        'precision': 0,
    },
    'rf-level': {
        'name': 'rf-level',
        'type': 'gauge',
        'unit': 'dBm',
        'label': 'Signalstyrka',
        'shortLabel': 'Signal',
        'longLabel': 'Signalstyrka',
        'precision': 0,
    }
}

// Populate fields, defaultFields, primaryFields, and fieldPrecisions
for (const mpType in settings.measuringPointTypes) {
    let index = 0
    const measurement = settings.measuringPointTypes[mpType].name
    measurementType[measurement] = mpType
    mpTypeFirstAlias[measurement] = settings.measuringPointTypes[mpType].aliases[0]
    for (const field of settings.measuringPointTypes[mpType].measurements) {
        if (index++ === 0) {
            defaultFields[measurement] = field.name
            defaultPrecisions[measurement] = field.precision
        }
        if (!fields[field.name]) {
            fields[field.name] = field
        }
       
        if (field.isPrimary) {
            if (!primaryFields[measurement]) {
                primaryFields[measurement] = []
            }
            primaryFields[measurement].push(field.name)
        }
        if (!measurementFields[measurement]) {
            measurementFields[measurement] = []
        }
        if (!field.isCalculated) {
            measurementFields[measurement].push(field.name)
        }
        if (fieldPrecisions[field.name] === undefined && field.precision !== undefined) {
            fieldPrecisions[field.name] = field.precision
        }
        seriesLabels[measurement + '.' + field.name] = field.label
    }
}

function getFieldDef(series) {
    const [ measurement, field ] = series.split('.')
    const mpType = Object.keys(settings.measuringPointTypes).find(key => settings.measuringPointTypes[key].name === measurement)
    return settings.measuringPointTypes[mpType].measurements.find(m => m.name === field)
}

const months = [ 'jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec' ]
const longMonths = [ 'januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december' ]

const daysOfWeek = {
    0: 'sön',
    1: 'mån',
    2: 'tis',
    3: 'ons',
    4: 'tor',
    5: 'fre',
    6: 'lör',
}

class SeriesHelpers {
    getMeasuringPointNameLabelPairs() {
        return Object.keys(settings.measuringPointTypes).map(key => {
            return {
                name: settings.measuringPointTypes[key].name,
                label: settings.measuringPointTypes[key].label
            }
        })
    }

    getMeasuringPointNameLabelMap() {
        const map = {}
        Object.keys(settings.measuringPointTypes).forEach(key => {
            map[settings.measuringPointTypes[key].name] = settings.measuringPointTypes[key].label
        })
        return map
    }

    getMeasurementFromType(type) {
        return settings.measuringPointTypes[type].name
    }

    getMeasurementLabel(measurement) {
        const mpType = Object.keys(settings.measuringPointTypes).find(key => settings.measuringPointTypes[key].name === measurement)
        return settings.measuringPointTypes[mpType].label
    }

    getMeasurementTypeHasCounterField(measurement) {
        const type = measurementType[measurement]
        if (type === undefined) {
            return false
        }
        return settings.measuringPointTypes[type].measurements.some(m => m.type === 'counter')
    }

    getMeasurementTypeCounterFieldNames(measurement) {
        const type = measurementType[measurement]
        if (type === undefined) {
            return []
        }
        return settings.measuringPointTypes[type].measurements.filter(m => m.type === 'counter').map(m => m.name)
    }

    getMeasurementTypeHasGaugeField(measurement) {
        const type = measurementType[measurement]
        if (type === undefined) {
            return false
        }  
        return settings.measuringPointTypes[type].measurements.some(m => m.type === 'gauge')
    }

    getMeasurementTypeGaugeFieldNames(measurement) {
        const type = measurementType[measurement]
        if (type === undefined) {
            return []
        }
        return settings.measuringPointTypes[type].measurements.filter(m => m.type === 'gauge').map(m => m.name)
    }

    getMeasurementLabelFromType(type) {
        return settings.measuringPointTypes[type].label
    }

    makeInitialLetterLowerCase(str) {
        return str.charAt(0).toLowerCase() + str.slice(1)
    }
    
    getFieldLabel(field, options) {
        return fields[field]?.label ? (options?.lowerCase ? this.makeInitialLetterLowerCase(fields[field].label) : fields[field].label) : field
    }

    getFieldShortLabel(field) {
        return fields[field]?.shortLabel ?? field
    }

    getFieldUnit(field) {
        return fields[field]?.unit ?? ''
    }

    getSeriesUnit(series) {
        return fields[series.split('.')[1]]?.unit ?? ''
    }

    getMeasurementDefaultUnit(measurement) {
        return fields[defaultFields[measurement]].unit
    }

    getMeasurementDefaultField(measurement) {
        return defaultFields[measurement]
    }

    getMeasurementPrimaryFields(measurement) {
        return primaryFields[measurement] || []
    }

    getFieldPrecision(field) {
        return fieldPrecisions[field] ?? 0
    }

    getMeasurementSortOrder(measurement) {
        const mpType = Object.keys(settings.measuringPointTypes).find(key => settings.measuringPointTypes[key].measurements.some(m => m.name === measurement))
        return mpType
    }

    getLongLabel(series) {
        const fieldDef = getFieldDef(series)
        return fieldDef?.longLabel ?? 'Okänd'
    }

    getLabelWithUnit(series, options = { upperCase: false }) {
        const fieldDef = getFieldDef(series)
        let longLabel
        if (fieldDef) {
            const unit = fieldDef.unit
            if (options.upperCase) {
                longLabel = fieldDef.longLabel.toUpperCase()
            } else {
                longLabel = fieldDef.longLabel
            }
            longLabel += ' (' + unit + ')'
        }
        return longLabel ?? 'Okänd'
    }

    getAllLabels() {
        const labels = {}
        for (const field in fields) {
            labels[field] = fields[field].label + ' (' + fields[field].unit + ')'
        }
        return labels
    }

    getColor(series) {
        return getFieldDef(series)?.color ?? '#000000'
    }

    getField(series) {
        return series.split('.')[1]
    }

    getXAxisLabel(date, interval, period = null) {
        switch (interval) {
            case '15min':
                return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
            case 'hour':
                if (period === 'week') {
                    return `${date.getHours().toString().padStart(2, '0')} ${date.getDate().toString()}/${(date.getMonth() + 1)}`
                }
                return `${date.getHours().toString().padStart(2, '0')}`
            case 'day':
                if (period === 'week') {
                    return `${daysOfWeek[date.getDay()]} ${date.getDate().toString()}/${(date.getMonth() + 1).toString()}`
                }
                return `${date.getDate().toString()}`
            case 'month':
                return months[date.getMonth()]
            default:
                return '??'
        }
    }

    formatValue(measurement, value) {
        if (typeof value === 'undefined') {
            return 'Saknas'
        }
        
        const precision = defaultPrecisions[measurement] ?? 0
        return Number.parseFloat(value).toLocaleString('sv', { minimumFractionDigits: precision, maximumFractionDigits: precision, useGrouping: false })
    }

    formatFieldValue(field, value, unit = false, def = '-') {
        let nbr
        if (isNaN(value)) {
            nbr = def
        } else {
            nbr = Number.parseFloat(value).toLocaleString('sv', {
                minimumFractionDigits: fieldPrecisions[field],
                maximumFractionDigits: fieldPrecisions[field],
                useGrouping: false
            })
        }
        return nbr + (unit ? (' ' + this.getFieldUnit(field)) : '')
    }

    formatSeriesValue(series, value, unit = false, def = '-') {
        const field = this.getField(series)
        return this.formatFieldValue(field, value, unit, def)
    }

    getMpTypeFirstAlias() {
        return mpTypeFirstAlias
    }

    getFieldChartType(field) {
        if (field === 'calculated-volume-flow') {
            return 'line'
        } else if (field === 'calculated-diff-temp') {
            return 'bar'
        }
        const fieldDef = fields[field]
        if (!fieldDef) {
            return 'bar'
        }
        switch (fieldDef.type) {
            case 'counter':
                return 'bar'
            case 'gauge':
            default:
                return 'line'
        }
    }

    getMonthLongName(monthIndex) {
        return longMonths[monthIndex]
    }

    getMeasurementFields(measurement) {
        return measurementFields[measurement] || []
    }

    getBillingFileFormats() {
        return settings.billingFileFormats
    }

    getSeriesLabel(series) {
        return seriesLabels[series]
    }
}

export default new SeriesHelpers()